import {
  grayColor,
  hexToRgb,
} from "assets/jss/material-dashboard-pro-react.js";

const dataExplorerStyle = (clientColor) => {
  const lightTint = clientColor
    ? `rgba(${hexToRgb(clientColor)}, 0.08)`
    : "rgba(0,0,0,0.03)";
  const selectedTint = clientColor
    ? `rgba(${hexToRgb(clientColor)}, 0.06)`
    : "rgba(0,0,0,0.025)";
  const accentBorder = clientColor
    ? `rgba(${hexToRgb(clientColor)}, 0.35)`
    : grayColor[8];

  return {
    // --- Layout ---
    container: {
      display: "flex",
      gap: "20px",
      alignItems: "flex-start",
      "@media (max-width: 960px)": {
        flexDirection: "column",
      },
    },
    leftPanel: {
      flex: "1 1 55%",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    rightPanel: {
      flex: "1 1 45%",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    // Grow last card in each panel to fill remaining space
    growCard: {
      flex: "1 1 auto",
    },

    // --- Section cards ---
    sectionCard: {
      marginTop: "0",
      marginBottom: "16px",
      borderRadius: "8px",
      overflow: "hidden",
      border: "1px solid " + grayColor[11],
      boxShadow: "none",
    },
    sectionCardBody: {
      padding: "0",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "9px 16px",
      backgroundColor: lightTint,
      borderBottom: "1px solid " + accentBorder,
    },
    sectionIcon: {
      color: clientColor || grayColor[2],
      fontSize: "18px",
      opacity: 0.8,
    },
    sectionTitle: {
      fontSize: "12px",
      fontWeight: 600,
      color: grayColor[2],
      margin: 0,
      textTransform: "uppercase",
      letterSpacing: "0.6px",
    },
    sectionContent: {
      padding: "12px 16px",
    },
    sectionDescription: {
      fontSize: "12px",
      color: grayColor[6],
      margin: "0 0 10px 0",
      lineHeight: "1.5",
    },

    // --- Period ---
    periodSection: {
      marginTop: "2px",
    },

    // --- Dimension Groups ---
    groupContainer: {
      marginBottom: "10px",
      "&:last-child": {
        marginBottom: "0",
      },
    },
    groupHeader: {
      fontSize: "10px",
      fontWeight: 600,
      color: grayColor[3],
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      padding: "5px 6px",
      marginBottom: "2px",
      backgroundColor: "#f7f7f7",
      borderRadius: "3px",
    },
    dimensionsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: "2px",
    },
    dimensionRow: {
      display: "flex",
      alignItems: "center",
      padding: "3px 6px 3px 10px",
      gap: "6px",
      flexWrap: "wrap",
      transition: "background-color 0.15s",
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: "rgba(0,0,0,0.02)",
      },
    },
    dimensionRowSelected: {
      backgroundColor: selectedTint,
    },
    dimensionCheckbox: {
      padding: "4px",
      marginRight: "2px",
      "& svg": {
        fontSize: "20px",
      },
      "& .MuiSvgIcon-root:not(.MuiCheckbox-checked *)": {
        color: grayColor[8],
      },
    },
    dimensionLabel: {
      fontSize: "12.5px",
      color: grayColor[2],
      cursor: "pointer",
      userSelect: "none",
    },
    filterButton: {
      fontSize: "10px",
      padding: "0px 8px",
      minHeight: "18px",
      textTransform: "none",
      lineHeight: "1.2",
      borderRadius: "9px",
    },
    filterTagWrapper: {
      display: "inline-flex",
      flexWrap: "wrap",
      gap: "3px",
      marginLeft: "2px",
    },

    // --- Values (P&L hierarchy) ---
    valuesGrid: {
      display: "flex",
      flexDirection: "column",
      border: "1px solid " + grayColor[11],
      borderRadius: "4px",
    },
    valueRow: {
      display: "flex",
      alignItems: "center",
      padding: "2px 10px 2px 14px",
      borderBottom: "1px solid " + grayColor[15],
      "&:last-child": {
        borderBottom: "none",
      },
    },
    valueRowEven: {
      backgroundColor: "#fafafa",
    },
    valueRowParent: {
      backgroundColor: "#f4f4f4",
    },
    valueRowMandatory: {
      backgroundColor: "#f0f0f0",
    },
    valueCheckbox: {
      padding: "2px",
      "& svg": {
        fontSize: "18px",
      },
      "& .MuiSvgIcon-root:not(.MuiCheckbox-checked *)": {
        color: grayColor[8],
      },
    },
    valueLabel: {
      fontSize: "12px",
      color: grayColor[2],
      cursor: "pointer",
      userSelect: "none",
    },
    valueLabelParent: {
      fontWeight: 600,
      fontSize: "12px",
    },
    valueLabelChild: {
      fontSize: "11.5px",
      color: grayColor[3],
    },
    valueLabelLevel2: {
      paddingLeft: "16px",
    },
    valueLabelLevel3: {
      paddingLeft: "32px",
    },
    valueActionLinks: {
      display: "flex",
      gap: "12px",
      marginBottom: "8px",
    },
    valueActionLink: {
      fontSize: "11px",
      color: grayColor[6],
      cursor: "pointer",
      whiteSpace: "nowrap",
      "&:hover": {
        color: grayColor[2],
      },
    },
    collapseToggle: {
      marginLeft: "auto",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      color: grayColor[6],
      padding: "0 2px",
      "&:hover": {
        color: grayColor[2],
      },
    },

    // --- Button disabled override (github color) ---
    feasibilityBtn: {
      "&.Mui-disabled, &:disabled": {
        backgroundColor: grayColor[9] + " !important",
        color: grayColor[6] + " !important",
        opacity: "1 !important",
        boxShadow: "none !important",
      },
    },

    // --- Feasibility & Download ---
    estimateBox: {
      padding: "12px 14px",
      borderRadius: "6px",
      backgroundColor: "#fafafa",
      border: "1px solid " + grayColor[11],
      marginTop: "12px",
    },
    estimateResult: {
      fontSize: "13px",
      margin: "0",
      lineHeight: "1.6",
    },
    estimateSuccess: {
      color: "#4caf50",
      fontWeight: 500,
    },
    estimateError: {
      color: "#f44336",
      fontWeight: 500,
    },
    estimateSuggestion: {
      fontSize: "12px",
      color: grayColor[6],
      margin: "4px 0 0 0",
    },
    downloadButtons: {
      display: "flex",
      gap: "8px",
      marginTop: "14px",
      flexWrap: "wrap",
    },
    downloadBtn: {
      fontSize: "12px",
      padding: "6px 16px",
      minHeight: "32px",
      textTransform: "none",
      fontWeight: 400,
      "& svg": {
        fontSize: "16px",
        marginRight: "4px",
      },
    },

    // --- Misc ---
    loadingWrapper: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      padding: "60px 0",
    },
    selectedCount: {
      fontSize: "11px",
      color: grayColor[6],
      marginTop: "8px",
      marginBottom: "0",
    },
  };
};

export default dataExplorerStyle;
